import { useState, useMemo } from 'react'
import { Photo } from "@/shared/api/photos"
import { PhotoInteractionsApi } from "@/shared/api"
import { PhotoGrid } from './PhotoGrid'
import { PhotoLightbox } from './PhotoLightbox'
import { useAuth } from "@/modules/auth"

interface PhotoGalleryProps {
  photos: Photo[]
  tripId: string
  coverPhotoId?: string | null
  onUpdatePhoto: (photoId: string, updateData: { caption?: string; takenAt?: string }) => Promise<void>
  onDeletePhoto: (photoId: string) => Promise<void>
  onSetCoverPhoto?: (photoId: string) => Promise<void>
  isUpdating: boolean
  isDeleting: boolean
}

export function PhotoGallery({ 
  photos, 
  tripId,
  coverPhotoId,
  onUpdatePhoto, 
  onDeletePhoto,
  onSetCoverPhoto, 
  isUpdating, 
  isDeleting 
}: PhotoGalleryProps) {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null)
  const { user, getAccessToken } = useAuth()

  // Create PhotoInteractionsApi instance
  const photoInteractionsApi = useMemo(() => {
    return new PhotoInteractionsApi(async () => {
      const token = await getAccessToken()
      return token
    })
  }, [getAccessToken])

  const handlePhotoClick = (photo: Photo) => {
    const index = photos.findIndex(p => p.id === photo.id)
    setSelectedPhotoIndex(index >= 0 ? index : null)
  }

  const handleNavigate = (newIndex: number) => {
    if (newIndex >= 0 && newIndex < photos.length) {
      setSelectedPhotoIndex(newIndex)
    }
  }

  const handleCloseLightbox = () => {
    setSelectedPhotoIndex(null)
  }

  const selectedPhoto = selectedPhotoIndex !== null ? photos[selectedPhotoIndex] : null

  return (
    <>
      <PhotoGrid
        photos={photos}
        onPhotoClick={handlePhotoClick}
        onDeletePhoto={onDeletePhoto}
        coverPhotoId={coverPhotoId}
        isDeleting={isDeleting}
      />

      {/* Lightbox with social features */}
      {selectedPhoto && selectedPhotoIndex !== null && (
        <PhotoLightbox
          photo={selectedPhoto}
          photos={photos}
          currentIndex={selectedPhotoIndex}
          onClose={handleCloseLightbox}
          onNavigate={handleNavigate}
          photoInteractionsApi={photoInteractionsApi}
          currentUserId={user?.id}
          onSetCoverPhoto={onSetCoverPhoto}
          coverPhotoId={coverPhotoId}
        />
      )}
    </>
  )
}
